import { Draft, Patch, nothing } from 'immer';
import { ReactiveVarManager } from './reactiveVarManager';
import { ImmerReactiveVar } from './makeImmerVar';
import { ReactiveVar } from '@apollo/client';
import { ImmerReactiveVarWithPatches } from './makeImmerVarWithPatches';
export type DraftedUpdaterFunction<S> = (state: S) => S;
export type DraftedUpdaterFunctionWithPatches<S> = (
  state: S
) => [S, Patch[], Patch[]];
export type UpdaterFunction<S> = (draft: Draft<S>) => void;
export type Updater<S> = S | UpdaterFunction<S>;

export const isUpdaterFunction = <S>(
  updater: Updater<S>
): updater is UpdaterFunction<S> => {
  return typeof updater === 'function';
};

type ProduceFn<T> = (updater: UpdaterFunction<T>) => DraftedUpdaterFunction<T>;
type ProduceFnWithPatches<T> = (
  updater: UpdaterFunction<T>
) => DraftedUpdaterFunctionWithPatches<T>;

export function getDraftedUpdater<T>(
  updaterOrValue: Updater<T>,
  produceFn: ProduceFn<T>
): DraftedUpdaterFunction<T>;
export function getDraftedUpdater<T>(
  updaterOrValue: Updater<T>,
  produceFn: ProduceFnWithPatches<T>
): DraftedUpdaterFunctionWithPatches<T>;
export function getDraftedUpdater<T>(
  updaterOrValue: Updater<T>,
  produceFn: ProduceFnWithPatches<T>
): DraftedUpdaterFunction<T> | DraftedUpdaterFunctionWithPatches<T> {
  if (isUpdaterFunction(updaterOrValue)) {
    return produceFn(updaterOrValue);
  }

  const value = updaterOrValue;
  const updater: UpdaterFunction<T> = (__draft) => {
    if (value === undefined) {
      return nothing;
    }
    return value;
  };

  return produceFn(updater);
}

export const registerImmerFunctions = <T>(
  immerReactiveVar: ImmerReactiveVar<T> | ImmerReactiveVarWithPatches<T>,
  reactiveVar: ReactiveVar<T>,
  initialValue: T
) => {
  immerReactiveVar.reset = () => reactiveVar(initialValue);
  immerReactiveVar.onNextChange = reactiveVar.onNextChange;
  immerReactiveVar.attachCache = (cache) => {
    reactiveVar.attachCache(cache);
    return immerReactiveVar;
  };
  immerReactiveVar.forgetCache = reactiveVar.forgetCache;
  ReactiveVarManager.add(immerReactiveVar);
};
